import { useState } from 'react';

import { useIsMounted } from '@hooks';
import { Button, Modal } from '@components';
import type { ButtonStyle } from '@components';

export type UseConfirmParams = {
  title?: string;
  loading?: boolean;
  confirmText?: string;
  confirmType?: ButtonStyle;
  text?: React.ReactNode;

  onConfirm?: () => unknown | Promise<unknown>;
};

type Result = {
  element: React.ReactNode;
  isOpened: boolean;
  close: () => void;
  open: (params?: UseConfirmParams) => void;
};

export const useConfirm = (params?: UseConfirmParams): Result => {
  const isMounted = useIsMounted();
  const [isOpened, setIsOpened] = useState(false);
  const [override, setOverride] = useState<UseConfirmParams>();
  const [localLoading, setLocalLoading] = useState(params?.loading || false);

  const settings: UseConfirmParams = override ||
    params || { onConfirm: () => void 0 };

  const handleClose = () => {
    setIsOpened(false);
    setOverride(undefined);
    setLocalLoading(false);
  };
  const handleOpen = (override?: UseConfirmParams) => {
    setIsOpened(true);
    setOverride(override);
    setLocalLoading(false);
  };

  return {
    element: (
      <Modal
        size="small"
        open={isOpened}
        onClose={handleClose}
        title={settings.title || 'Confirm'}
        bottomActions={
          <>
            <Button
              label={settings.confirmText || 'Yes'}
              type={settings.confirmType || 'primary'}
              loading={settings?.loading || localLoading}
              onClick={async () => {
                setLocalLoading(true);
                await settings.onConfirm?.();
                if (!isMounted.current) return;

                handleClose();
              }}
            />
            <Button onClick={handleClose}>Cancel</Button>
          </>
        }
      >
        <div className="text-medium">{settings.text}</div>
      </Modal>
    ),
    isOpened,

    close: handleClose,
    open: handleOpen,
  };
};
